import { useEffect, useState } from "react";
import { X, MessageCircle, Compass, Anchor, Info } from "lucide-react";
import { Header, Footer, FloatingWhatsApp } from "@/components/SitePage";
import { destinations, zonesInOrder, experiencesInOrder, byZone, byExperience, type Destination } from "@/data/destinations";
import { restaurantsByDestination, type Restaurant } from "@/data/restaurants";
import { WHATSAPP_LINK } from "@/content/site";
import type { Lang } from "@/content/site";

function PhotoLightbox({ r, lang, onClose }: { r: Restaurant | null; lang: Lang; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    if (r) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [r, onClose]);
  if (!r) return null;
  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/80 p-6"
      onClick={(e) => { e.stopPropagation(); onClose(); }}
      role="dialog"
      aria-label={r.name}
    >
      <figure className="max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
        <img src={r.photo} alt={r.name} className="w-full h-auto rounded shadow-2xl" />
        <figcaption className="mt-3 flex items-start justify-between gap-4 text-white">
          <span>
            <span className="font-display text-lg">{r.name}</span>
            <span className="block text-xs text-white/70">{r.place}</span>
          </span>
          <button
            onClick={onClose}
            aria-label={lang === "en" ? "Close" : "Fechar"}
            className="shrink-0 rounded-full p-2 hover:bg-white/10"
          >
            <X className="h-5 w-5" />
          </button>
        </figcaption>
      </figure>
    </div>
  );
}

function DestinationPanel({ d, lang, onClose }: { d: Destination | null; lang: Lang; onClose: () => void }) {
  const [photo, setPhoto] = useState<Restaurant | null>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    if (d) {
      window.addEventListener("keydown", onKey);
      document.body.style.overflow = "hidden";
    }
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [d, onClose]);
  if (!d) return null;
  const t = (en: string, pt: string) => (lang === "en" ? en : pt);
  const restaurants = restaurantsByDestination[d.id] ?? [];
  const wa = lang === "en"
    ? `Hello! I would like to enquire about a private cruise from Vilamoura that visits ${d.name}.`
    : `Olá! Gostaria de saber sobre um cruzeiro privado a partir de Vilamoura que passe em ${d.name}.`;
  return (
    <div className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <article
        role="dialog"
        aria-label={d.name}
        onClick={(e) => e.stopPropagation()}
        className="absolute top-0 right-0 h-full w-full max-w-2xl overflow-y-auto bg-white shadow-2xl"
      >
        <header className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-border bg-white/95 backdrop-blur px-8 py-5">
          <div>
            <p className="text-[11px] uppercase tracking-[0.24em] text-emerald-deep/70 font-medium">
              {d.zoneLabel} · {d.nm} nm {t("from Vilamoura", "de Vilamoura")}
            </p>
            <h2 className="font-display text-3xl leading-tight mt-1 text-emerald-deep">{d.name}</h2>
            <p className="text-muted-foreground italic mt-1">{lang === "en" ? d.subtitle.en : d.subtitle.pt}</p>
          </div>
          <button aria-label={t("Close", "Fechar")} onClick={onClose} className="shrink-0 rounded-full p-2 hover:bg-muted">
            <X className="h-5 w-5" />
          </button>
        </header>
        <div className="px-8 py-8 space-y-8">
          <p className="font-display text-lg leading-relaxed text-foreground/85">
            {lang === "en" ? d.storyEN : d.storyPT}
          </p>
          <section>
            <p className="text-[11px] uppercase tracking-[0.24em] text-emerald-deep/70 font-medium mb-3">{t("On this stop", "Nesta paragem")}</p>
            <ul className="space-y-1.5 text-sm">
              {(lang === "en" ? d.highlights.en : d.highlights.pt).map((h) => (
                <li key={h} className="flex gap-2"><span className="text-gold">◆</span><span>{h}</span></li>
              ))}
            </ul>
          </section>
          <section className="text-sm border-y border-border py-5">
            <div>
              <p className="text-[11px] uppercase tracking-[0.24em] text-emerald-deep/70 font-medium mb-1">{t("Distance", "Distância")}</p>
              <p className="tabular-nums">{d.nm} nm</p>
            </div>
          </section>
          {restaurants.length > 0 && (
            <section>
              <p className="text-[11px] uppercase tracking-[0.24em] text-emerald-deep/70 font-medium mb-3">
                {t("Restaurants on this stop", "Restaurantes nesta paragem")}
              </p>
              <ul className="space-y-4">
                {restaurants.map((r) => (
                  <li key={r.id} className="border border-border rounded p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-medium text-emerald-deep">{r.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {r.place} · {lang === "en" ? r.cuisine.en : r.cuisine.pt}
                          {r.rating ? ` · ${r.rating} ★` : ""}
                        </p>
                      </div>
                      <button
                        onClick={() => setPhoto(r)}
                        aria-label={t(`View photo of ${r.name}`, `Ver fotografia de ${r.name}`)}
                        title={t("View photo", "Ver fotografia")}
                        className="shrink-0 rounded-full border border-emerald-deep/25 p-1.5 text-emerald-deep hover:bg-emerald-deep hover:text-white transition"
                      >
                        <Info className="h-4 w-4" />
                      </button>
                    </div>
                    <p className="mt-2 text-sm text-foreground/80 leading-relaxed">
                      {lang === "en" ? r.summary.en : r.summary.pt}
                    </p>
                  </li>
                ))}
              </ul>
            </section>
          )}
          <div className="flex flex-wrap gap-3 pt-2">
            <a href={WHATSAPP_LINK(wa)} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-emerald-deep text-white px-6 py-3 text-xs font-semibold uppercase tracking-[0.2em] hover:bg-emerald-deep/90">
              <MessageCircle className="h-4 w-4" /> {t("Enquire on WhatsApp", "Falar via WhatsApp")}
            </a>
          </div>
          {d.sources.length > 0 && (
            <section>
              <p className="text-[11px] uppercase tracking-[0.24em] text-emerald-deep/70 font-medium mb-3">{t("Official sources", "Fontes oficiais")}</p>
              <ul className="space-y-1 text-xs text-foreground/70">
                {d.sources.map((s) => (
                  <li key={s.url}><a href={s.url} target="_blank" rel="noopener noreferrer" className="hover:text-emerald-deep underline underline-offset-2">{s.label}</a></li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </article>
      <PhotoLightbox r={photo} lang={lang} onClose={() => setPhoto(null)} />
    </div>
  );
}

function DestRow({ d, lang, onClick }: { d: Destination; lang: Lang; onClick: () => void }) {
  return (
    <li>
      <button onClick={onClick} className="w-full text-left py-2.5 hover:bg-white/60 transition rounded px-2 -mx-2 group flex items-baseline justify-between gap-4">
        <span>
          <span className="font-medium text-emerald-deep group-hover:text-gold transition">{d.name}</span>
          <span className="block text-xs text-muted-foreground italic mt-0.5">{lang === "en" ? d.subtitle.en : d.subtitle.pt}</span>
        </span>
        <span className="tabular-nums text-xs text-muted-foreground shrink-0">{d.nm} nm</span>
      </button>
    </li>
  );
}

export function DestinationsPage({ lang }: { lang: Lang }) {
  const [active, setActive] = useState<Destination | null>(null);
  const [tab, setTab] = useState<"route" | "experience">("route");
  const t = (en: string, pt: string) => (lang === "en" ? en : pt);
  return (
    <div className="min-h-screen bg-sand">
      <Header lang={lang} />

      {/* HERO */}
      <section className="relative pt-40 pb-20 bg-emerald-deep text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_30%_20%,theme(colors.gold)_0%,transparent_50%)]" />
        <div className="relative max-w-6xl mx-auto px-6">
          <p className="text-[11px] uppercase tracking-[0.3em] text-gold/90 font-medium">
            {t("Marina de Vilamoura · Piers 21 & 22", "Marina de Vilamoura · Cais 21 e 22")}
          </p>
          <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl mt-6 leading-[1.05] max-w-4xl">
            {t("Forty stretches of Algarve.", "Quarenta pedaços de Algarve.")}<br />
            <span className="italic text-gold">{t("One yacht, quietly yours.", "Um iate, discretamente seu.")}</span>
          </h1>
          <p className="mt-8 max-w-2xl text-base sm:text-lg text-white/85 leading-relaxed">
            {t(
              "From Marina de Vilamoura, most of the Algarve coast is inside a comfortable day. Choose a cave, a fishing village, a beach with a restaurant on the sand — or a Moorish castle reached only from the river. We take you there, in a private cruise aboard the Morningstar.",
              "A partir da Marina de Vilamoura, quase toda a costa algarvia cabe num dia tranquilo. Escolha uma gruta, uma aldeia de pescadores, uma praia com restaurante na areia — ou um castelo mouro a que só se chega pelo rio. Levamo-lo lá, em cruzeiro privado a bordo do Morningstar.",
            )}
          </p>
        </div>
      </section>

      {/* THE IDEA */}
      <section className="max-w-4xl mx-auto px-6 py-20">
        <p className="text-[11px] uppercase tracking-[0.24em] text-emerald-deep/70 font-medium">{t("The idea", "A ideia")}</p>
        <h2 className="font-display text-3xl md:text-5xl mt-3 leading-tight text-emerald-deep">
          {t("The Algarve reads differently from a private deck.", "O Algarve lê-se de outra forma a partir de um convés privado.")}
        </h2>
        <div className="mt-6 space-y-4 text-lg leading-relaxed text-foreground/85 max-w-2xl">
          <p>{t(
            "From Marina de Vilamoura, most of the Algarve coast is inside a comfortable day. West, the cliffs rise into caves and honey-coloured arches. East, the Ria Formosa opens into a hundred kilometres of protected lagoon and barrier islands — voted one of Portugal's 7 Natural Wonders.",
            "A partir da Marina de Vilamoura, quase toda a costa algarvia cabe num dia tranquilo. Para poente, as arribas erguem-se em grutas e arcos cor de mel. Para nascente, a Ria Formosa abre-se em cem quilómetros de laguna protegida e ilhas-barreira — eleita uma das 7 Maravilhas Naturais de Portugal.",
          )}</p>
          <p>{t(
            "Below is our guide to forty destinations we sail to — with distances and landing options. Every source is official. Nothing embellished.",
            "Em baixo está o nosso guia dos quarenta destinos a que navegamos — com distâncias e opções de desembarque. Todas as fontes são oficiais. Nada embelezado.",
          )}</p>
        </div>
      </section>

      {/* THE COASTLINE */}
      <section id="coastline" className="bg-sand py-20 border-t border-border">
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-[11px] uppercase tracking-[0.24em] text-emerald-deep/70 font-medium">{t("The coastline", "A costa")}</p>
          <h2 className="font-display text-3xl md:text-5xl mt-2 leading-tight text-emerald-deep">
            {t("Where you can go.", "Onde se pode ir.")}
          </h2>
          <p className="mt-4 text-muted-foreground max-w-2xl">
            {t(
              "Forty destinations from Marina de Vilamoura. Tap any name to open the full guide — story, highlights and distance.",
              "Quarenta destinos a partir da Marina de Vilamoura. Toque num nome para abrir o guia completo — história, destaques e distância.",
            )}
          </p>
          <div className="mt-8 inline-flex rounded-full border border-emerald-deep/20 bg-white p-1 text-xs font-medium">
            <button onClick={() => setTab("route")}
              className={`px-4 py-1.5 rounded-full transition inline-flex items-center gap-1.5 ${tab === "route" ? "bg-emerald-deep text-white" : "text-emerald-deep/70"}`}>
              <Anchor className="h-3.5 w-3.5" />{t("By route", "Por rota")}
            </button>
            <button onClick={() => setTab("experience")}
              className={`px-4 py-1.5 rounded-full transition inline-flex items-center gap-1.5 ${tab === "experience" ? "bg-emerald-deep text-white" : "text-emerald-deep/70"}`}>
              <Compass className="h-3.5 w-3.5" />{t("By experience", "Por experiência")}
            </button>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
            {tab === "route"
              ? zonesInOrder.map((z) => (
                  <section key={z.id}>
                    <h3 className="font-display text-xl font-bold uppercase tracking-wide text-gold">{lang === "en" ? z.en : z.pt}</h3>
                    <ul className="mt-3 divide-y divide-emerald-deep/10">
                      {byZone(z.id).map((d) => <DestRow key={d.id} d={d} lang={lang} onClick={() => setActive(d)} />)}
                    </ul>
                  </section>
                ))
              : experiencesInOrder.map((e) => (
                  <section key={e.id}>
                    <h3 className="font-display text-xl font-bold uppercase tracking-wide text-gold">{lang === "en" ? e.en : e.pt}</h3>
                    <p className="mt-1 text-sm text-muted-foreground italic">{lang === "en" ? e.blurb.en : e.blurb.pt}</p>
                    <ul className="mt-3 divide-y divide-emerald-deep/10">
                      {byExperience(e.id).map((d) => <DestRow key={d.id} d={d} lang={lang} onClick={() => setActive(d)} />)}
                    </ul>
                  </section>
                ))}
          </div>
        </div>
      </section>

      <DestinationPanel d={active} lang={lang} onClose={() => setActive(null)} />
      <Footer lang={lang} />
      <FloatingWhatsApp lang={lang} />
    </div>
  );
}

// JSON-LD builders — TouristAttraction per destination + TouristTrip per zone + FAQPage
export function buildDestinationsSchemas(lang: Lang, pageUrl: string) {
  const t = (en: string, pt: string) => (lang === "en" ? en : pt);
  const attractions = destinations.map((d) => ({
    "@type": "TouristAttraction",
    name: d.name,
    description: lang === "en" ? d.storyEN : d.storyPT,
    geo: { "@type": "GeoCoordinates", latitude: d.coords.lat, longitude: d.coords.lng },
    address: { "@type": "PostalAddress", addressRegion: "Algarve", addressCountry: "PT" },
    isAccessibleForFree: true,
    touristType: ["Luxury travelers", "Yacht charter guests"],
  }));
  const trips = zonesInOrder.map((z) => {
    const zoneDests = byZone(z.id);
    if (!zoneDests.length) return null;
    const maxNm = Math.max(...zoneDests.map((d) => d.nm));
    return {
      "@type": "TouristTrip",
      name: t(`Private yacht cruise from Vilamoura to ${z.en}`, `Cruzeiro privado de Vilamoura a ${z.pt}`),
      description: t(
        `Private charter aboard Morningstar from Marina de Vilamoura to ${z.en} (${maxNm} nm one-way).`,
        `Charter privado a bordo do Morningstar da Marina de Vilamoura para ${z.pt} (${maxNm} milhas náuticas ida).`,
      ),
      touristType: ["Luxury travelers"],
      itinerary: zoneDests.map((d) => ({
        "@type": "Place",
        name: d.name,
        geo: { "@type": "GeoCoordinates", latitude: d.coords.lat, longitude: d.coords.lng },
      })),
      provider: { "@type": "Organization", name: "Vilamoura Dreams Cruises", url: "https://vilamouradreamscruises.com" },
    };
  }).filter(Boolean);
  const faqs = [
    { q: t("Where do cruises depart from?", "De onde partem os cruzeiros?"),
      a: t("All cruises depart from Marina de Vilamoura, piers 21 and 22, aboard the Morningstar.", "Todos os cruzeiros partem da Marina de Vilamoura, cais 21 e 22, a bordo do Morningstar.") },
    { q: t("How far is the Benagil cave from Vilamoura?", "A que distância fica a Gruta de Benagil de Vilamoura?"),
      a: t("14 nautical miles (~26 km), roughly 1 h 15 at cruising speed.", "14 milhas náuticas (~26 km), cerca de 1 h 15 à velocidade de cruzeiro.") },
    { q: t("Can we reach the Ria Formosa on a day cruise?", "Consegue-se chegar à Ria Formosa num cruzeiro de um dia?"),
      a: t("Yes — Faro–Olhão inlet is 12 nm east, Farol island 15 nm, Tavira 24 nm. Long Day cruises reach as far as Cacela Velha (28 nm).", "Sim — a barra Faro-Olhão fica a 12 nm para leste, a Ilha do Farol a 15 nm, Tavira a 24 nm. Os cruzeiros Long Day chegam a Cacela Velha (28 nm).") },
    { q: t("Which destinations are only reachable by sea?", "Que destinos só se alcançam por mar?"),
      a: t("Gruta de Benagil, Alfanzina and Contrabandista caves, Praia da Marinha coves, Praia dos Arrifes, Ilha Deserta and most of Ria Formosa's barrier islands.", "Gruta de Benagil, grutas de Alfanzina e do Contrabandista, enseadas da Praia da Marinha, Praia dos Arrifes, Ilha Deserta e a maior parte das ilhas-barreira da Ria Formosa.") },
  ];
  return [
    { "@context": "https://schema.org", "@graph": attractions },
    { "@context": "https://schema.org", "@graph": trips },
    { "@context": "https://schema.org", "@type": "FAQPage", url: pageUrl,
      mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) },
  ];
}